import React, { useMemo } from 'react';
import { getColorNameFromHex } from '../utils/colorNames';
import { deriveSuggestedColors, isValidHex, isFullHex } from '../utils/colorPalette';

const ColorPaletteEditor = ({
  palette,
  onPaletteChange,
  onRequestRemove,
  minColors = 1
}) => {
  const savedCount = palette.filter((color) => isValidHex(color.hex)).length;
  const suggestedColors = useMemo(() => deriveSuggestedColors(palette), [palette]);

  const updatePaletteColor = (index, field, value) => {
    onPaletteChange(palette.map((color, idx) => {
      if (idx !== index) return color;
      if (field === 'hex') {
        const nextHex = String(value || '').trim();
        const hasFullHex = isFullHex(nextHex);
        return {
          ...color,
          hex: value,
          name: hasFullHex ? getColorNameFromHex(nextHex) : (color.name || '')
        };
      }
      return { ...color, [field]: value };
    }));
  };

  const addPaletteColor = () => {
    if (palette.length >= 6) return;
    onPaletteChange([...palette, { hex: '', name: '' }].slice(0, 6));
  };

  const addSuggestedColor = (color) => {
    if (palette.length >= 6) return;
    const alreadyExists = palette.some(
      (existing) => String(existing.hex).toLowerCase() === color.hex.toLowerCase()
    );
    if (alreadyExists) return;
    onPaletteChange([...palette, color].slice(0, 6));
  };

  return (
    <>
      <div className="flex items-center justify-between gap-3 mb-3">
        <label className="block text-sm font-bold text-gray-700">
          Color Palette
        </label>
        <span className="text-xs font-semibold text-gray-500">Up to 6 colors</span>
      </div>
      <div className="space-y-3 overflow-hidden">
        {palette.map((color, index) => (
          <div key={index} className="flex items-center gap-2">
            {(() => {
              const hasColor = isValidHex(color.hex);
              const pickerValue = isFullHex(color.hex) ? color.hex : '#ffffff';
              return (
                <label
                  className="group relative h-10 w-10 shrink-0 cursor-pointer overflow-hidden rounded-xl border border-gray-200 shadow-inner transition hover:border-primary/40"
                  style={{
                    backgroundColor: hasColor ? color.hex : '#ffffff',
                    backgroundImage: hasColor
                      ? 'none'
                      : 'linear-gradient(45deg, #e5e7eb 25%, transparent 25%), linear-gradient(-45deg, #e5e7eb 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #e5e7eb 75%), linear-gradient(-45deg, transparent 75%, #e5e7eb 75%)',
                    backgroundSize: '10px 10px',
                    backgroundPosition: '0 0, 0 5px, 5px -5px, -5px 0px'
                  }}
                  title="Pick color"
                >
                  <span className="pointer-events-none absolute inset-0 bg-white/0 transition group-hover:bg-white/35" />
                  <span
                    className={`pointer-events-none absolute inset-0 flex items-center justify-center text-xl font-black text-gray-900 transition ${
                      hasColor ? 'opacity-0 group-hover:opacity-100' : 'opacity-80'
                    }`}
                  >
                    +
                  </span>
                  <input
                    type="color"
                    value={pickerValue}
                    onChange={(e) => {
                      updatePaletteColor(index, 'hex', e.target.value);
                      e.target.blur();
                    }}
                    className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                    aria-label={`Pick ${color.name || 'color'}`}
                  />
                </label>
              );
            })()}
            <input
              type="text"
              value={color.name}
              onChange={(e) => updatePaletteColor(index, 'name', e.target.value)}
              className="min-w-0 h-10 flex-1 rounded-xl border border-gray-200 px-3 text-sm font-medium text-gray-800 outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
              placeholder="Color name"
            />
            <input
              type="text"
              value={color.hex}
              onChange={(e) => updatePaletteColor(index, 'hex', e.target.value)}
              className="h-10 w-[6.75rem] shrink-0 rounded-xl border border-gray-200 px-2.5 text-sm font-mono text-gray-700 outline-none focus:border-primary focus:ring-4 focus:ring-primary/10 sm:w-28"
              placeholder="#HEX"
            />
            <button
              type="button"
              onClick={() => onRequestRemove?.(index)}
              disabled={savedCount <= minColors && isValidHex(color.hex)}
              className="h-9 w-9 shrink-0 rounded-full text-gray-400 transition hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-30"
              aria-label={`Remove ${color.name || 'color'}`}
            >
              <svg className="mx-auto h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 12h12" />
              </svg>
            </button>
          </div>
        ))}
        {palette.length < 6 && (
          <button
            type="button"
            onClick={addPaletteColor}
            className="flex min-h-11 w-full items-center justify-center gap-2 rounded-xl border border-dashed border-gray-300 bg-gray-50 text-sm font-bold text-gray-600 transition hover:border-primary/40 hover:bg-primary/5 hover:text-primary"
          >
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white text-lg leading-none shadow-sm">+</span>
            Add custom color
          </button>
        )}
      </div>
      <div className="mt-5">
        <p className="mb-2 text-xs font-bold uppercase tracking-[0.16em] text-gray-500">
          Suggested from this palette
        </p>
        <div className="flex flex-wrap gap-2">
          {suggestedColors.map((color) => (
            <button
              key={color.hex}
              type="button"
              onClick={() => addSuggestedColor(color)}
              disabled={palette.length >= 6}
              className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-bold text-gray-700 transition hover:border-primary/30 hover:bg-primary/5 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <span className="h-4 w-4 rounded-full border border-gray-200" style={{ backgroundColor: color.hex }} />
              {color.name}
            </button>
          ))}
        </div>
      </div>
    </>
  );
};

export default ColorPaletteEditor;
